import type { Attachment } from '@medplum/fhirtypes'
import { toast, useMedplum, useQuery } from '@seen/react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ccdaBinaryReference } from '#src/pages/ccda/ccdaBinaryReference.ts'
import { ccdaToPrintableHtml } from '#src/pages/ccda/ccdaToPrintableHtml.ts'
import {
  openCcdaPrintWindow,
  printHtmlDocument,
} from '#src/pages/ccda/printHtmlDocument.ts'

interface UsePrintCcdaResult {
  printCcda: (attachment: Attachment) => Promise<void>
  isPrinting: boolean
}

interface CcdaDownloader {
  download: (url: string) => Promise<{ text: () => Promise<string> }>
}

function ccdaXmlQueryKey(url: string): readonly ['ccda-print-xml', string] {
  return ['ccda-print-xml', url]
}

async function downloadCcdaXml(
  medplum: CcdaDownloader,
  url: string
): Promise<string> {
  const data = await medplum.download(ccdaBinaryReference(url))
  return data.text()
}

export function usePrintCcda(
  attachmentUrl?: string | null
): UsePrintCcdaResult {
  const medplum = useMedplum()
  const queryClient = useQueryClient()

  // Prefetch so Print PDF can convert and print in the same click. Chrome
  // expires transient user activation in about a second, so awaiting a Binary
  // download inside the click handler silently drops the print dialog.
  useQuery({
    // oxlint-disable-next-line @tanstack/query/exhaustive-deps -- medplum is stable
    queryKey: ccdaXmlQueryKey(attachmentUrl ?? 'none'),
    queryFn: () => downloadCcdaXml(medplum, attachmentUrl ?? ''),
    enabled: Boolean(attachmentUrl),
    staleTime: Number.POSITIVE_INFINITY,
  })

  const mutation = useMutation({
    mutationFn: async (attachment: Attachment) => {
      const url = attachment.url
      if (!url) {
        throw new Error('No CCDA document attached to this export')
      }

      const cachedXml = queryClient.getQueryData<string>(ccdaXmlQueryKey(url))
      if (cachedXml) {
        printHtmlDocument(ccdaToPrintableHtml(cachedXml))
        return
      }

      const printWindow = openCcdaPrintWindow()
      try {
        const xml = await queryClient.fetchQuery({
          // oxlint-disable-next-line @tanstack/query/exhaustive-deps -- medplum is stable
          queryKey: ccdaXmlQueryKey(url),
          queryFn: () => downloadCcdaXml(medplum, url),
          staleTime: Number.POSITIVE_INFINITY,
        })
        printHtmlDocument(ccdaToPrintableHtml(xml), printWindow)
      } catch (error) {
        printWindow?.close()
        throw error
      }
    },
    onError: (error) => {
      console.error('Error printing CCDA:', error)
      toast.error('Could not prepare this CCDA for printing')
    },
  })

  return {
    printCcda: (attachment) => mutation.mutateAsync(attachment),
    isPrinting: mutation.isPending,
  }
}
